import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollToFirstError, scrollToFirstErrorSoon } from "../../../Shared/scrollToFirstError";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Alert, Button, Chip, CircularProgress, IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { DownloadOutlined, PersonOutline, RefreshOutlined } from "@mui/icons-material";
import { Formik, Form, FastField } from "formik";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { DialogFrame } from "../../Policy/BriskCertificateList/BriskV2Dialogs";
import FormSection from "../../Policy/OnlinePolicy/components/FormSection";
import { CHOICE_TOGGLE_SX, FONT, NAVY, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../Policy/OnlinePolicy/components/formStyles";
import "../../Policy/OnlinePolicy/components/onlinePolicyForm.scss";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import { AccountBalanceWalletIcon } from "../../../Resources/Icons/icons";
import { createAttemptTracker, formatINR, shouldReuseRequestId } from "../../AddMoneyToUser/addMoneyUtils";
import { INDIAN_STATES } from "../indianStates";
import { ROADZEN_CERT_LIST_PATH } from "../roadzenPaths";
import {
  createRoadzenCertificate,
  getRoadzenCertConfig,
  getRoadzenCertDownloadUrl,
  getRoadzenCertificate,
  getRoadzenCertPolicy,
  listRoadzenCertPolicies,
  saveRoadzenCertDraft,
  updateRoadzenCertificate,
} from "../../../Service/RoadzenService";
import PdfFilesPicker from "./PdfFilesPicker";
import {
  MODE_NEW,
  MODE_POLICY,
  UPDATE_LOCKED_FIELDS,
  attemptFingerprint,
  buildSchema,
  formToValues,
  initialValues,
  isSafeLink,
  policyOptionLabel,
  productLabel,
  splitPrice,
  valuesToForm,
} from "./roadzenCertUtils";

/**
 * RoadZen Create Certificate (owner, 2026-11-03: "exactly like Brisk"): the Brisk Create Certificate page's layout
 * (section cards, the New policy / Already created policy switch, the sticky bar with Save Draft + Create, a spinner
 * on the pressed button, the success dialog with Download), with RoadZen's own fields and the insurer's policy PDF
 * RoadZen needs. The same page in update mode is Edit (= RoadZen's endorsement): only the changed fields are sent.
 *
 * Money: the agency's Company Wallet "RoadZen" is charged before RoadZen is asked (refunded once if RoadZen refuses).
 */

const labelSx = { mb: 0.5, fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 500 };
const hintSx = { fontFamily: FONT, fontSize: 12, color: "#667085" };
const BALANCE_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: 36,
  px: 1.5,
  borderRadius: "8px",
  border: "1px solid #D6E1EE",
  backgroundColor: "#EEF3F9",
  color: NAVY,
  fontFamily: FONT,
  fontSize: "13px",
  fontWeight: 600,
  "& svg": { fontSize: 18 },
};
const BAR_BUTTON_SX = { height: 38, fontSize: "13px", minWidth: { xs: 104, sm: 140 } };

const Label = ({ children, optional = false }) => (
  <Typography sx={labelSx}>
    {children} {!optional && <span style={{ color: "red" }}>*</span>}
  </Typography>
);
const FieldError = ({ name, errors, touched }) => (
  <div className="errorMessage mb-2 mt-2">{errors[name] && touched[name] ? <div>{errors[name]}</div> : " "}</div>
);
const toOptions = (list) => (list || []).map((v) => ({ label: v, value: v }));
const pickerValue = (v) => (v && dayjs(v).isValid() ? dayjs(v) : null);
const LEGAL_HEIR = ["true", "false"];

/*
 * Typing speed (owner: "the field enter has lag"): every field redraws only when ITS value changes. Text boxes are
 * Formik FastFields; dropdowns and date pickers are memoised; the form is checked when a field is left and on Save /
 * Create, not on every key.
 */
const UpperField = React.memo(function UpperField({ name, disabled = false }) {
  return (
    <FastField name={name} key={disabled ? "locked" : "open"}>
      {({ field, form }) => (
        <input {...field} value={field.value || ""} className="textField w-100" disabled={disabled} onChange={(e) => form.setFieldValue(name, e.target.value.toUpperCase())} />
      )}
    </FastField>
  );
});
const ChoiceField = React.memo(function ChoiceField({ name, options, value, setFieldValue }) {
  const opts = useMemo(() => toOptions(options), [options]);
  return (
    <Autocomplete
      className="AutoComplete_InputBox"
      options={opts}
      isOptionEqualToValue={(o, v) => o?.value === v}
      value={value || null}
      onChange={(e, v) => setFieldValue(name, v?.label || "")}
      renderInput={(params) => <TextField {...params} />}
    />
  );
});
const notFuture = (d) => d && d.isAfter(dayjs().endOf("day"));
const notPast = (d) => d && d.isBefore(dayjs().startOf("day"));
const DateField = React.memo(function DateField({ name, value, setFieldValue, disabled = false, futureOnly = false }) {
  return (
    <DatePicker
      name={name}
      className="Date_Picker w-100"
      placeholder="Select Date or Enter DD/MM/YYYY"
      value={pickerValue(value)}
      disabled={disabled}
      onChange={(d) => setFieldValue(name, d ? d.format("YYYY-MM-DD") : "")}
      format={"DD/MM/YYYY"}
      placement="bottomRight"
      disabledDate={futureOnly ? notPast : notFuture}
    />
  );
});
const PolicyField = React.memo(function PolicyField({ policies, selected, onPick }) {
  const value = selected ? policies.find((p) => p.id === selected.id) || selected : null;
  return (
    <Autocomplete
      className="AutoComplete_InputBox"
      options={policies}
      getOptionLabel={(p) => (p ? policyOptionLabel(p) : "")}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={value}
      onChange={(e, v) => onPick(v ? v.id : null)}
      noOptionsText="No policy with a saved policy PDF is waiting for a RoadZen certificate"
      renderInput={(params) => <TextField {...params} placeholder="Choose an Online Policy" />}
    />
  );
});
/**
 * One part of the form that redraws only when one of its `deps` changes, so a key typed in one box does not redraw the
 * other sections. The text boxes inside still update themselves (they read the form directly). List in `deps` every
 * value the part shows.
 */
const sameDeps = (a, b) => a.deps.length === b.deps.length && a.deps.every((d, i) => Object.is(d, b.deps[i]));
const Keep = React.memo(({ children }) => <>{children}</>, sameDeps);

const ProductField = React.memo(function ProductField({ products, value, setFieldValue, disabled }) {
  const product = products.find((p) => String(p.id) === String(value)) || null;
  return (
    <Autocomplete
      className="AutoComplete_InputBox"
      options={products}
      getOptionLabel={(p) => productLabel(p)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled}
      value={product}
      onChange={(e, p) => setFieldValue("productId", p ? String(p.id) : "")}
      renderInput={(params) => <TextField {...params} placeholder="Choose RoadZen's product" />}
    />
  );
});

const RoadzenCreateCertificate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const formElRef = useRef(null); // the <form>: a check made after Formik's (policy, PDF) scrolls to its message too
  const actionRef = useRef("create");
  const trackerRef = useRef(createAttemptTracker());
  // a ref, not state: two clicks in the same moment both see the old state, never the old ref
  const sendingRef = useRef(false);

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [mode, setMode] = useState(MODE_NEW);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyError, setPolicyError] = useState("");
  const [files, setFiles] = useState([]);
  const [savedPdfs, setSavedPdfs] = useState([]); // a draft's PDFs (on our S3)
  const [fileError, setFileError] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [updateId, setUpdateId] = useState(null);
  const [updateNumber, setUpdateNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [reading, setReading] = useState(false);
  const [sendingAction, setSendingAction] = useState("");
  const [success, setSuccess] = useState(null);

  const isUpdate = Boolean(updateId);
  const can = (config && config.can) || {};
  const readOnly = !config || (!isUpdate && !can.create) || (isUpdate && !can.edit);
  const products = useMemo(() => (config && config.products) || [], [config]);
  const lists = (config && config.lists) || { genders: [], relations: [], fuels: [] };
  const schema = useMemo(() => buildSchema({ cpaProductIds: products.filter((p) => p.includesCpa).map((p) => p.id), isUpdate }), [products, isUpdate]);

  const loadConfig = useCallback(() => {
    getRoadzenCertConfig().then((res) => {
      if (res.error) {
        setConfigError(res.message);
        return;
      }
      setConfig(res.data);
      setConfigError(res.data.problem || res.data.productsProblem || "");
    });
  }, []);
  useEffect(() => loadConfig(), [loadConfig]);

  const fill = (form, extra) => formRef.current && formRef.current.setValues(formToValues(form, extra), false);

  // what opened the page: Edit or Continue (from the Certificate List)
  useEffect(() => {
    const state = (location.state && location.state.roadzenCert) || null;
    if (!state || !state.certificateId) return;
    setReading(true);
    getRoadzenCertificate(state.certificateId)
      .then((res) => {
        if (res.error) return ToastError(res.message);
        const r = res.data;
        const extra = { productId: r.productId ? String(r.productId) : "", policyPrice: r.totalPremium || "", riskStartDate: r.riskStart || "" };
        if (state.mode === "update") {
          setUpdateId(r.id);
          setUpdateNumber(r.certificateNumber);
        } else {
          setDraftId(r.id);
          setSavedPdfs(r.insurerPdfs || []);
          if (r.mode === MODE_POLICY && r.source && r.source.onlinePolicyId) setMode(MODE_POLICY);
        }
        // wait a tick: Formik is mounted by now
        setTimeout(() => fill(r.form, extra), 0);
        return undefined;
      })
      .finally(() => setReading(false));
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== MODE_POLICY || !config) return;
    listRoadzenCertPolicies().then((res) => {
      if (res.error) setPolicyError(res.message);
      else setPolicies(res.data || []);
    });
  }, [mode, config]);

  const choosePolicy = async (id) => {
    setPolicyError("");
    if (!id) {
      setSelectedPolicy(null);
      return;
    }
    setReading(true);
    const res = await getRoadzenCertPolicy(id);
    setReading(false);
    if (res.error) {
      setPolicyError(res.message);
      return;
    }
    setSelectedPolicy(res.data.policy);
    const v = formRef.current ? formRef.current.values : initialValues;
    fill(res.data.form, { productId: v.productId, policyPrice: v.policyPrice, riskStartDate: v.riskStartDate });
  };

  // the picker keeps one function for good, so typing elsewhere never redraws it
  const chooseRef = useRef(choosePolicy);
  chooseRef.current = choosePolicy;
  const pickPolicy = useCallback((id) => chooseRef.current(id), []);

  const switchMode = (next) => {
    setMode(next);
    setSelectedPolicy(null);
    setPolicyError("");
    setFiles([]);
    setFileError("");
    trackerRef.current.reset();
    if (formRef.current) formRef.current.resetForm({ values: initialValues });
  };

  const formData = (values, extra = {}) => {
    const fd = new FormData();
    fd.append("mode", mode);
    fd.append("productId", values.productId);
    fd.append("policyPrice", String(values.policyPrice).trim());
    fd.append("riskStartDate", values.riskStartDate);
    fd.append("form", JSON.stringify(valuesToForm(values)));
    if (mode === MODE_POLICY && selectedPolicy) fd.append("onlinePolicyId", selectedPolicy.id);
    if (draftId) fd.append("draftId", draftId);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append("policy_copy", f, f.name));
    return fd;
  };

  const createNow = async (values) => {
    if (mode === MODE_POLICY && !selectedPolicy) {
      setPolicyError("Please choose the policy");
      ToastError("Please choose the policy");
      scrollToFirstErrorSoon(formElRef.current);
      return;
    }
    if (mode === MODE_NEW && !files.length && !savedPdfs.length) {
      setFileError("Upload the insurer's policy PDF. RoadZen needs it with every certificate");
      scrollToFirstErrorSoon(formElRef.current);
      return;
    }
    const requestId = trackerRef.current.idFor({ userId: attemptFingerprint(values, mode, selectedPolicy && selectedPolicy.id), amount: values.policyPrice });
    const res = await createRoadzenCertificate(formData(values, { requestId }));
    if (!res.error) {
      trackerRef.current.reset();
      setSuccess({ message: res.message, certificateNo: res.data.certificateNumber, id: res.data.id, hasPdf: res.data.hasPdf || res.data.status === "SOLD" });
      loadConfig();
      return;
    }
    if (!shouldReuseRequestId(res)) trackerRef.current.reset();
    ToastError(res.message);
    loadConfig();
  };

  const onSubmit = async (values) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    const action = actionRef.current;
    setSending(true);
    setSendingAction(action);
    try {
      if (action === "draft") {
        const res = await saveRoadzenCertDraft(formData(values));
        if (res.error) ToastError(res.message);
        else {
          setDraftId(res.data.id);
          setSavedPdfs(res.data.insurerPdfs || []);
          setFiles([]);
          ToastSuccess(res.message);
        }
      } else if (action === "update") {
        const res = await updateRoadzenCertificate(updateId, { form: valuesToForm(values), reason: values.reason });
        if (res.error) ToastError(res.message);
        else {
          ToastSuccess(res.message);
          navigate(ROADZEN_CERT_LIST_PATH);
        }
      } else {
        await createNow(values);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
      setSendingAction("");
    }
  };

  const startAnother = () => {
    setSuccess(null);
    setDraftId(null);
    setSavedPdfs([]);
    setFiles([]);
    setSelectedPolicy(null);
    trackerRef.current.reset();
    if (formRef.current) formRef.current.resetForm({ values: initialValues });
  };

  const openSavedCertificate = async () => {
    // a tab opened at click time is never blocked as a popup; it is pointed at the PDF when the link arrives
    const tab = window.open("", "_blank");
    const res = await getRoadzenCertDownloadUrl(success.id);
    if (res.error || !res.data || !isSafeLink(res.data.url)) {
      if (tab) tab.close();
      ToastError(res.message || "The certificate PDF is not available right now");
      return;
    }
    if (tab) tab.location.href = res.data.url;
    else window.open(res.data.url, "_blank", "noopener,noreferrer");
  };

  const locked = (name) => isUpdate && UPDATE_LOCKED_FIELDS.includes(name);
  const onFilesChange = useCallback((next) => {
    setFiles(next);
    setFileError("");
  }, []);

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container className="Master_Header_Container" sx={{ px: { xs: 1, sm: 2 } }}>
          <Grid item xs={12} sm={6}>
            <Typography className="Master_Header_Heading" sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
              {isUpdate ? "RoadZen Edit Certificate" : "RoadZen Create Certificate"}
              {config && config.apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} />
        </Grid>
      </Box>
      <Box className="op-form-page" sx={{ px: { xs: 0.5, sm: 2 }, pt: 2, pb: 1 }}>
        {configError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {configError}
          </Alert>
        )}
        {config && config.sellerProblem && !isUpdate && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {config.sellerProblem}
          </Alert>
        )}
        {config && !isUpdate && !can.create && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Your login can only view RoadZen certificates Because you dont have the seperate wallet for zen company
          </Alert>
        )}

        <Box sx={{ height: 3, mb: 0.5 }}>{reading && <LinearProgress aria-label="Reading the details" sx={{ height: 3, borderRadius: 2 }} />}</Box>
        <Formik initialValues={initialValues} validationSchema={schema} innerRef={formRef} onSubmit={onSubmit} validateOnChange={false}>
          {({ errors, touched, values, setFieldValue }) => {
            const product = products.find((p) => String(p.id) === String(values.productId)) || null;
            const cpa = Boolean(product && product.includesCpa);
            const split = splitPrice(values.policyPrice, values.proposer_state);
            return (
              <Form className="op-form op-policy" ref={formElRef}>
                {/* a Create / Save refused by the form's checks scrolls to the first field with an error (owner, 2026-11-04) */}
                <ScrollToFirstError />
                {!isUpdate && (
                  <Keep deps={[mode, policies, selectedPolicy, policyError]}>
                    <FormSection title="Certificate for" testId="roadzen-certificate-for">
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, mb: 2 }}>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={mode}
                          aria-label="New policy or already created policy"
                          onChange={(e, next) => {
                            if (next) switchMode(next);
                          }}
                          sx={CHOICE_TOGGLE_SX}
                        >
                          <ToggleButton value={MODE_NEW}>New policy</ToggleButton>
                          <ToggleButton value={MODE_POLICY}>Already created policy</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography sx={hintSx}>
                          {mode === MODE_NEW
                            ? "Type the details and upload the insurer's policy PDF. Charged to the Company Wallet \"RoadZen\"."
                            : "An Online Policy with its saved policy PDF. Its details fill the form; its PDF is sent to RoadZen. Charged to the Company Wallet \"RoadZen\"."}
                        </Typography>
                      </Box>
                      {mode === MODE_POLICY && (
                        <Grid container columnSpacing={3}>
                          <Grid item xs={12} sm={8}>
                            <Label>Online Policy</Label>
                            <PolicyField policies={policies} selected={selectedPolicy} onPick={pickPolicy} />
                            <div className="errorMessage mb-2 mt-2">{policyError || " "}</div>
                          </Grid>
                        </Grid>
                      )}
                    </FormSection>
                  </Keep>
                )}

                <Keep deps={[errors, touched, cpa, lists, values.proposer_dob, values.proposer_gender, values.proposer_state]}>
                  <FormSection title="Customer Details">
                    <Grid container columnSpacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Label>Customer Name</Label>
                        <UpperField name="proposer_name" />
                        <FieldError name="proposer_name" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label>Mobile</Label>
                        <FastField className="textField w-100" name="proposer_mobile" inputMode="numeric" maxLength={10} />
                        <FieldError name="proposer_mobile" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Email</Label>
                        <FastField className="textField w-100" name="proposer_email" />
                        <FieldError name="proposer_email" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional={!cpa}>Date of Birth</Label>
                        <DateField name="proposer_dob" value={values.proposer_dob} setFieldValue={setFieldValue} />
                        <FieldError name="proposer_dob" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional={!cpa}>Gender</Label>
                        <ChoiceField name="proposer_gender" options={lists.genders} value={values.proposer_gender} setFieldValue={setFieldValue} />
                        <FieldError name="proposer_gender" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional={!cpa}>Address Line 1</Label>
                        <UpperField name="address_line1" />
                        <FieldError name="address_line1" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Address Line 2</Label>
                        <UpperField name="address_line2" />
                        <FieldError name="address_line2" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>City</Label>
                        <UpperField name="proposer_city" />
                        <FieldError name="proposer_city" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>State</Label>
                        <ChoiceField name="proposer_state" options={INDIAN_STATES} value={values.proposer_state} setFieldValue={setFieldValue} />
                        <Typography component="small" sx={{ ...hintSx, display: "block", mt: 0.5 }}>
                          Decides the GST: Uttar Pradesh = CGST + SGST, other states IGST
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Pincode</Label>
                        <FastField className="textField w-100" name="proposer_pincode" inputMode="numeric" maxLength={6} />
                        <FieldError name="proposer_pincode" errors={errors} touched={touched} />
                      </Grid>
                    </Grid>
                  </FormSection>
                </Keep>

                <Keep deps={[errors, touched, cpa, lists, values.nominee_relationship, values.nominee_gender, values.nominee_dob, values.nominee_is_legal_heir]}>
                  <FormSection title="Nominee Details" description={cpa ? "Needed for a product with CPA" : "Needed only for a product with CPA"}>
                    <Grid container columnSpacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Label optional={!cpa}>Nominee Name</Label>
                        <UpperField name="nominee_name" />
                        <FieldError name="nominee_name" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional={!cpa}>Nominee Relationship</Label>
                        <ChoiceField name="nominee_relationship" options={lists.relations} value={values.nominee_relationship} setFieldValue={setFieldValue} />
                        <FieldError name="nominee_relationship" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional={!cpa}>Nominee Gender</Label>
                        <ChoiceField name="nominee_gender" options={lists.genders} value={values.nominee_gender} setFieldValue={setFieldValue} />
                        <FieldError name="nominee_gender" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Nominee Date of Birth</Label>
                        <DateField name="nominee_dob" value={values.nominee_dob} setFieldValue={setFieldValue} />
                        <FieldError name="nominee_dob" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Nominee is Legal Heir</Label>
                        <ChoiceField name="nominee_is_legal_heir" options={LEGAL_HEIR} value={values.nominee_is_legal_heir} setFieldValue={setFieldValue} />
                      </Grid>
                    </Grid>
                  </FormSection>
                </Keep>

                <Keep deps={[errors, touched, lists, isUpdate, values.fuel_type]}>
                  <FormSection title="Vehicle Details">
                    <Grid container columnSpacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Make</Label>
                        <UpperField name="vehicle_make" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Model</Label>
                        <UpperField name="vehicle_model" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Fuel Type</Label>
                        <ChoiceField name="fuel_type" options={lists.fuels} value={values.fuel_type} setFieldValue={setFieldValue} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Registration Number</Label>
                        <UpperField name="registration_number" disabled={locked("registration_number")} />
                        <FieldError name="registration_number" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label>Chassis Number</Label>
                        <UpperField name="chassis_number" disabled={locked("chassis_number")} />
                        <FieldError name="chassis_number" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Engine Number</Label>
                        <UpperField name="engine_number" disabled={locked("engine_number")} />
                      </Grid>
                    </Grid>
                    {isUpdate && <Typography sx={{ ...hintSx, mb: 1 }}>Chassis, engine and registration numbers cannot be changed. A different vehicle needs a new certificate.</Typography>}
                  </FormSection>
                </Keep>

                <Keep deps={[errors, touched, isUpdate, products, product, config, values.productId, values.riskStartDate, split.net, split.gst, split.kind]}>
                  <FormSection title="Premium Details">
                    <Grid container columnSpacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Label>Product</Label>
                        <ProductField products={products} value={values.productId} setFieldValue={setFieldValue} disabled={locked("productId")} />
                        {product && (
                          <Typography component="small" sx={{ ...hintSx, display: "block", mt: 0.5 }}>
                            {[product.includesRsa && "Roadside assistance", product.includesCpa && "CPA (needs the date of birth, gender, address and nominee)"].filter(Boolean).join(" + ")} · {product.validity}
                          </Typography>
                        )}
                        <FieldError name="productId" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label>Risk Start Date</Label>
                        <DateField name="riskStartDate" value={values.riskStartDate} setFieldValue={setFieldValue} disabled={locked("riskStartDate")} futureOnly />
                        <Typography component="small" sx={{ ...hintSx, display: "block", mt: 0.5 }}>
                          Today or later. Cover runs one year from it
                        </Typography>
                        <FieldError name="riskStartDate" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label>Total Premium</Label>
                        <FastField key={locked("policyPrice") ? "locked" : "open"} name="policyPrice" type="number" className="textField w-100" disabled={locked("policyPrice")} />
                        <FieldError name="policyPrice" errors={errors} touched={touched} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Label optional>Net Premium</Label>
                        <input className="textField w-100" name="netPremium" type="number" value={split.net} readOnly style={{ backgroundColor: "#F9FAFB", color: "#667085" }} />
                        {split.net !== "" && (
                          <small style={{ color: NAVY, display: "block", fontFamily: FONT, marginTop: 4 }}>
                            Net Premium: {split.net}, GST ({split.kind}): {split.gst}
                          </small>
                        )}
                      </Grid>
                    </Grid>

                    {!isUpdate && (
                      <Grid container spacing={1} alignItems="center" sx={{ mt: 0, mb: 1.5 }}>
                        <Grid item xs={12} sm="auto">
                          <Box sx={BALANCE_SX}>
                            <AccountBalanceWalletIcon />
                            <span>Wallet Balance = {config && config.walletBalance !== null && config.walletBalance !== undefined ? formatINR(config.walletBalance) : "not available"}</span>
                          </Box>
                        </Grid>
                        {/* a user login pays from his own Policy Wallet too (owner, 2026-11-03); the server sends it only for him */}
                        {config && config.policyWallet && (
                          <Grid item xs={12} sm="auto">
                            <Tooltip title="A user login pays from his own Policy Wallet too, like his Online Policies">
                              <Box sx={BALANCE_SX} data-testid="policy-wallet-balance">
                                <AccountBalanceWalletIcon />
                                <span>Your Policy Wallet = {config.policyWallet.balance === null ? "not available" : formatINR(config.policyWallet.balance)}</span>
                              </Box>
                            </Tooltip>
                          </Grid>
                        )}
                        {config && config.seller && (
                          <Grid item xs={12} sm="auto">
                            <Tooltip title="RoadZen issues every certificate under its one active user and takes the price from that user's RoadZen wallet">
                              <Box sx={BALANCE_SX}>
                                <PersonOutline />
                                <span>
                                  Issued under {config.seller.name} ({config.seller.code})
                                  {config.seller.balancePaise !== null ? ` · RoadZen wallet ${formatINR(config.seller.balancePaise / 100)}` : ""}
                                </span>
                              </Box>
                            </Tooltip>
                          </Grid>
                        )}
                        <Grid item xs={12} sm="auto">
                          <Tooltip title="Refresh Balance">
                            <IconButton onClick={loadConfig} aria-label="refresh" sx={{ color: NAVY }}>
                              <RefreshOutlined />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </FormSection>
                </Keep>

                {!isUpdate && (
                  <Keep deps={[mode, selectedPolicy, files, savedPdfs, sending, readOnly, fileError, config]}>
                    <FormSection title="Insurer Policy PDF" description="RoadZen needs the insurer's policy PDF with every certificate. It is saved here too">
                      {mode === MODE_POLICY ? (
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#344054", mb: 1.5 }}>
                          {selectedPolicy ? `The saved PDF of policy ${selectedPolicy.policyNo} (${selectedPolicy.pdfName}) is sent to RoadZen.` : "Choose the Online Policy; its saved policy PDF is sent to RoadZen."}
                        </Typography>
                      ) : (
                        <Box sx={{ mb: 1.5 }}>
                          <PdfFilesPicker
                            files={files}
                            saved={savedPdfs}
                            disabled={sending || readOnly}
                            error={fileError}
                            max={(config && config.limits && config.limits.maxPdfs) || 10}
                            maxMb={(config && config.limits && config.limits.maxPdfMb) || 25}
                            onChange={onFilesChange}
                          />
                        </Box>
                      )}
                    </FormSection>
                  </Keep>
                )}

                {isUpdate && (
                  <Keep deps={[errors, touched, updateNumber]}>
                    <FormSection title="Reason for the change" description={`RoadZen re-issues ${updateNumber} with the corrected details. Only the fields you changed are sent. Nothing is charged`}>
                      <Grid container columnSpacing={3}>
                        <Grid item xs={12} sm={8}>
                          <Label>Reason</Label>
                          <FastField className="textField w-100" name="reason" placeholder="e.g. Name spelling" />
                          <FieldError name="reason" errors={errors} touched={touched} />
                        </Grid>
                      </Grid>
                    </FormSection>
                  </Keep>
                )}

                {/* the buttons stay at the bottom of the window while the page scrolls (.op-action-bar in onlinePolicyForm.scss) */}
                <Box className="op-action-bar" data-testid="form-action-bar">
                  <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
                    {(sendingAction === "create" || sendingAction === "update") && (
                      <Typography role="status" sx={{ ...hintSx, fontSize: 12.5, mr: "auto" }}>
                        {sendingAction === "create" ? "Asking RoadZen to create the certificate. This can take up to a minute." : "Asking RoadZen to re-issue the certificate. This can take up to a minute."}
                      </Typography>
                    )}
                    {/* a user login has no drafts (owner, 2026-11-03): the server says so with can.saveDraft */}
                    {!isUpdate && can.saveDraft !== false && (
                      <Button
                        onClick={() => {
                          actionRef.current = "draft";
                        }}
                        variant="outlined"
                        size="small"
                        type="submit"
                        disabled={sending || reading || readOnly}
                        startIcon={sendingAction === "draft" ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{ ...SECONDARY_BUTTON_SX, ...BAR_BUTTON_SX }}
                      >
                        Save Draft
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        actionRef.current = isUpdate ? "update" : "create";
                      }}
                      variant="contained"
                      size="small"
                      type="submit"
                      disabled={sending || reading || readOnly || Boolean(configError)}
                      startIcon={sendingAction === "create" || sendingAction === "update" ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={{ ...PRIMARY_BUTTON_SX, ...BAR_BUTTON_SX }}
                    >
                      {isUpdate ? "Update Certificate" : "Create Certificate"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>

      {success && (
        <DialogFrame
          title="Certificate Created"
          subtitle={success.certificateNo ? `Certificate ${success.certificateNo}` : ""}
          onClose={() => setSuccess(null)}
          footer={
            <>
              <Button variant="outlined" sx={{ textTransform: "none", borderRadius: "6px" }} onClick={() => navigate(ROADZEN_CERT_LIST_PATH)}>
                Open Certificate List
              </Button>
              <Button variant="outlined" sx={{ textTransform: "none", borderRadius: "6px" }} onClick={startAnother}>
                Create Another
              </Button>
            </>
          }
        >
          <Typography sx={{ fontSize: 13.5, color: "#344054", mb: 2 }}>{success.message}</Typography>
          <Button variant="contained" disableElevation onClick={openSavedCertificate} startIcon={<DownloadOutlined />} sx={{ textTransform: "none", borderRadius: "6px", alignSelf: "flex-start" }}>
            Download Certificate
          </Button>
        </DialogFrame>
      )}
    </>
  );
};

export default RoadzenCreateCertificate;
